import { ArrowUpRight, Network, X } from "lucide-react";
import { Link } from "react-router-dom";

import { layerAgentFor, layerAgents } from "../../data/layerAgents";
import type { GraphSnapshot } from "../../domain/model";

interface SnapshotProps {
  snapshot: GraphSnapshot;
}

export function LayerLeadRoster({ snapshot }: SnapshotProps) {
  return (
    <section className="layer-leads-home" aria-labelledby="layer-leads-title">
      <header className="section-heading">
        <div>
          <p className="eyebrow">NINE LAYER LEADS / 47 OWNED NODES</p>
          <h2 id="layer-leads-title">Every layer has an accountable owner.</h2>
        </div>
        <p>Each lead owns one computation layer and every capability node inside it. Together they operate the complete graph.</p>
      </header>
      <div className="layer-lead-grid">
        {layerAgents.map((agent) => {
          const nodes = snapshot.nodes.filter((node) => node.layer === agent.layer);
          return (
            <Link
              className="layer-lead-card"
              key={agent.id}
              style={{ "--agent-accent": agent.accent } as React.CSSProperties}
              to={`/agents/${agent.id}`}
              aria-label={`Open full profile for ${agent.name}, ${agent.layer} Layer Lead, managing ${nodes.length} nodes`}
            >
              <div className="layer-lead-portrait">
                <img src={agent.image} alt={`${agent.name}, PWEB ${agent.layer} Layer Lead`} loading="lazy" decoding="async" />
                <span>{agent.index}</span>
              </div>
              <div className="layer-lead-card-copy">
                <p>{agent.layer} / {nodes.length} nodes</p>
                <h3>{agent.name}</h3>
                <span>{agent.role}</span>
              </div>
              <ArrowUpRight aria-hidden="true" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}

interface ConsoleProps extends SnapshotProps {
  activeLayer: string;
  onLayer: (layer: string) => void;
}

export function LayerLeadConsole({ snapshot, activeLayer, onLayer }: ConsoleProps) {
  const activeAgent = layerAgentFor(activeLayer);
  const activeNodes = activeAgent ? snapshot.nodes.filter((node) => node.layer === activeAgent.layer) : snapshot.nodes;

  return (
    <section className="layer-lead-console" aria-label="Layer Lead ownership console">
      <div className="lead-console-summary">
        {activeAgent ? (
          <>
            <Link className="lead-console-profile-link" to={`/agents/${activeAgent.id}`} aria-label={`Open full profile for ${activeAgent.name}`}>
              <img src={activeAgent.image} alt="" decoding="async" />
            </Link>
            <div>
              <p className="eyebrow">ACTIVE OWNER / {activeAgent.index}</p>
              <h2>{activeAgent.name} <span>{activeAgent.layer} Lead</span></h2>
              <p>{activeAgent.mandate}</p>
            </div>
            <strong>{activeNodes.length}<span>owned nodes</span></strong>
            <button className="icon-button" type="button" onClick={() => onLayer("")} aria-label="Show all Layer Leads"><X /></button>
          </>
        ) : (
          <>
            <div className="lead-console-network"><Network aria-hidden="true" /></div>
            <div>
              <p className="eyebrow">ALL LAYERS / COORDINATION MODE</p>
              <h2>Nine leads <span>One civilization graph</span></h2>
              <p>Select a lead to inspect her layer and the capability nodes under direct ownership.</p>
            </div>
            <strong>{activeNodes.length}<span>total nodes</span></strong>
          </>
        )}
      </div>
      <div className="lead-selector" role="list" aria-label="Select a Layer Lead">
        {layerAgents.map((agent) => {
          const nodeCount = snapshot.nodes.filter((node) => node.layer === agent.layer).length;
          return (
            <div key={agent.id} role="listitem">
              <button
                type="button"
                className={activeLayer === agent.layer ? "active" : ""}
                style={{ "--agent-accent": agent.accent } as React.CSSProperties}
                onClick={() => onLayer(agent.layer)}
                aria-pressed={activeLayer === agent.layer}
                aria-label={`${agent.name}, ${agent.layer} Layer Lead, ${nodeCount} nodes`}
                title={`${agent.name}: ${agent.layer} / ${nodeCount} nodes`}
              >
                <img src={agent.image} alt="" loading="lazy" decoding="async" />
                <span><b>{agent.index}</b>{agent.layer}<small>{nodeCount} nodes</small></span>
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
